import { ApolloError } from '@apollo/client'
import { parseApolloError } from '@eversports/react-components/logical/parse-apollo-error'
import parseExpectedError from '@eversports/react-components/logical/parse-expected-error'
import safeRedirect from '@eversports/react-components/utilities/safe-redirect'
import { ClientError } from '@eversports/react-components/client-error'
import { parse } from 'query-string'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'

import { AuthRoutes } from '../auth-routes'
import {
  EversportsAndVenueConsentForm,
  EversportsConsentBlockingUpdateForm,
  EversportsConsentForm,
  EversportsConsentUpdateForm,
} from '../components/ConsentForms'
import { PolicyVariant } from '../components/ConsentForms/EversportsConsentUpdateForm'
import handleAuthResult, { AuthResult } from '../utils/handle-auth-result'
import { normalizePolicyUrls } from '../utils/normalize-policy-urls'
import { getPoliciesToShow } from '../utils/get-policies-to-show'
import { decorateLinkWithGAParameters } from '../utils/decorate-link-with-g-a-parameters'
import getRedirectTo from '../utils/get-redirect-to'
import {
  PolicyType,
  useConsentScreenAcceptEversportsAndVenuePoliciesMutation,
  useConsentScreenAcceptEversportsPoliciesMutation,
  useConsentScreenCurrentPoliciesQuery,
  useConsentScreenDismissPoliciesMutation,
  useConsentScreenEversportsAndVenueCurrentPoliciesQuery,
} from '../graphql'

interface VenueAndEversportsConsentProps {
  onCompleted: (result: AuthResult) => void
  handleConsentRejection: () => void
  venueId: string
}

const VenueAndEversportsConsent = ({
  onCompleted,
  handleConsentRejection,
  venueId,
}: VenueAndEversportsConsentProps) => {
  const location = useLocation()
  const [error, setError] = useState<ClientError | undefined>()

  const [consentMutation, { loading: mutationLoading }] = useConsentScreenAcceptEversportsAndVenuePoliciesMutation({
    variables: { venueId },
    onCompleted: ({ acceptEversportsPolicies, acceptVenuePolicies }) => {
      if (acceptEversportsPolicies.__typename === 'ExpectedErrors') {
        return setError(parseExpectedError(acceptEversportsPolicies.errors))
      }
      if (acceptVenuePolicies.__typename === 'ExpectedErrors') {
        return setError(parseExpectedError(acceptVenuePolicies.errors))
      }
      return onCompleted(acceptEversportsPolicies)
    },
    onError: (e: ApolloError) => {
      setError(parseApolloError(e))
    },
  })

  const { data } = useConsentScreenEversportsAndVenueCurrentPoliciesQuery({
    variables: { venueId },
  })

  return data ? (
    <EversportsAndVenueConsentForm
      eversportsPolicies={normalizePolicyUrls(data.latestEversportsPolicies)}
      companyPolicies={normalizePolicyUrls(data.venue.company.latestPolicies)}
      venueName={data.venue.name}
      onConsentAcceptance={consentMutation}
      loading={mutationLoading}
      onConsentRejection={handleConsentRejection}
      error={error}
      errorUrl={`/register?${location.search}`}
    />
  ) : null
}

interface EversportsConsentProps {
  onCompleted: (result: AuthResult) => void
  handleConsentRejection: () => void
  onPoliciesDismissed: () => void
}

type CurrentPoliciesData = NonNullable<ReturnType<typeof useConsentScreenCurrentPoliciesQuery>['data']>
type VisiblePolicy = NonNullable<CurrentPoliciesData['me']>['missingEversportsPolicies'][number]

const EversportsConsent = ({ onCompleted, handleConsentRejection, onPoliciesDismissed }: EversportsConsentProps) => {
  const [error, setError] = useState<ClientError | undefined>()
  const [isAccepting, setIsAccepting] = useState(false)
  const [pendingResult, setPendingResult] = useState<AuthResult | null>(null)
  const lastPoliciesRef = useRef<Array<VisiblePolicy>>([])
  const location = useLocation()

  const [consentMutation, { loading: mutationLoading }] = useConsentScreenAcceptEversportsPoliciesMutation({
    onCompleted: ({ acceptEversportsPolicies }) => {
      if (acceptEversportsPolicies.__typename === 'ExpectedErrors') {
        setIsAccepting(false)
        return setError(parseExpectedError(acceptEversportsPolicies.errors))
      }
      setPendingResult(acceptEversportsPolicies)
    },
    onError: (e: ApolloError) => {
      setIsAccepting(false)
      setError(parseApolloError(e))
    },
  })

  useEffect(() => {
    if (pendingResult) {
      onCompleted(pendingResult)
    }
  }, [pendingResult, onCompleted])

  const [dismissMutation, { loading: dismissLoading }] = useConsentScreenDismissPoliciesMutation({
    onCompleted: onPoliciesDismissed,
    onError: (e: ApolloError) => {
      setError(parseApolloError(e))
    },
  })

  const { data } = useConsentScreenCurrentPoliciesQuery()

  if (!data?.me) return null

  const visiblePolicies = getPoliciesToShow(data.me.missingEversportsPolicies)
  if (visiblePolicies.length > 0) {
    lastPoliciesRef.current = visiblePolicies
  }

  const effectivePolicies =
    visiblePolicies.length > 0 ? visiblePolicies : isAccepting ? lastPoliciesRef.current : visiblePolicies

  if (effectivePolicies.length === 0) return null

  const normalizedPolicies = normalizePolicyUrls(effectivePolicies)
  const isUpdate = effectivePolicies.every((policy) => policy.isUpdate)
  const isBlocking = effectivePolicies.some((policy) => policy.isBlocking)

  const handleConsentAcceptance = () => {
    setIsAccepting(true)
    consentMutation().catch(() => undefined)
  }

  const handleRemindMeLater = () =>
    dismissMutation({ variables: { policyIds: effectivePolicies.map((policy) => policy.id) } })

  const errorUrl = `/register?${location.search}`
  const loading = mutationLoading || dismissLoading || isAccepting

  if (isUpdate) {
    const hasTerms = normalizedPolicies.some((policy) => policy.type === PolicyType.POLICY_TYPE_TERMS_OF_SERVICE)
    const hasPrivacy = normalizedPolicies.some((policy) => policy.type === PolicyType.POLICY_TYPE_PRIVACY)
    const variant: PolicyVariant = hasTerms && hasPrivacy ? 'both' : hasTerms ? 'terms' : 'privacy'
    const termsUrl = normalizedPolicies.find((policy) => policy.type === PolicyType.POLICY_TYPE_TERMS_OF_SERVICE)?.url
    const privacyUrl = normalizedPolicies.find((policy) => policy.type === PolicyType.POLICY_TYPE_PRIVACY)?.url

    return isBlocking ? (
      <EversportsConsentBlockingUpdateForm
        variant={variant}
        termsUrl={termsUrl}
        privacyUrl={privacyUrl}
        onConsentAcceptance={handleConsentAcceptance}
        onConsentRejection={handleConsentRejection}
        loading={loading}
        error={error}
        errorUrl={errorUrl}
      />
    ) : (
      <EversportsConsentUpdateForm
        variant={variant}
        termsUrl={termsUrl}
        privacyUrl={privacyUrl}
        onConsentAcceptance={handleConsentAcceptance}
        onRemindMeLater={handleRemindMeLater}
        loading={loading}
        error={error}
        errorUrl={errorUrl}
      />
    )
  }

  return (
    <EversportsConsentForm
      policies={normalizedPolicies}
      onConsentAcceptance={handleConsentAcceptance}
      loading={loading}
      onConsentRejection={handleConsentRejection}
      error={error}
      errorUrl={errorUrl}
    />
  )
}

const ConsentScreen = () => {
  const location = useLocation()
  const history = useHistory()

  const { venueId, redirectApp, origin } = parse(location.search) as Partial<{
    venueId: string
    redirectApp: string
    origin: string
  }>

  const onCompleted = async (result: AuthResult) => {
    const { n: nextLocation } = parse(history.location.search) as { n: string }

    return handleAuthResult({ result, history, nextLocation })
  }

  const handleConsentRejection = () => {
    safeRedirect(history, AuthRoutes.LOGIN)
  }

  const onPoliciesDismissed = () => {
    const { n: nextLocation } = parse(history.location.search) as { n: string }
    if (nextLocation) {
      safeRedirect(history, { pathname: nextLocation })
    } else {
      window.location.href = decorateLinkWithGAParameters(getRedirectTo(history.location))
    }
  }

  return venueId && redirectApp === 'widget' && origin === 'widget' ? (
    <VenueAndEversportsConsent
      venueId={venueId}
      onCompleted={onCompleted}
      handleConsentRejection={handleConsentRejection}
    />
  ) : (
    <EversportsConsent
      onCompleted={onCompleted}
      handleConsentRejection={handleConsentRejection}
      onPoliciesDismissed={onPoliciesDismissed}
    />
  )
}

export default ConsentScreen
